<template>
    <vx-modal :visible="props.visible" @update:visible="emit('update:visible', $event)" :padding="false" size="md">
        <template v-slot:header>
            <div class="p-6">Schedule Status Update</div>
        </template>

        <form
            id="schedule-group-status-update"
            @submit.prevent="submit"
            class="px-6 py-12 flex flex-col items-center gap-2"
        >
            <div class="text-center text-sm text-slate-700" data-test="schedule-date-preview">
                {{ timingDescription }}
            </div>
            <vx-date-picker
                v-model="date"
                mode="date"
                :timezone="ET_TIMEZONE"
                :minDate="bounds.minDate"
                :maxDate="bounds.maxDate"
                isRequired
            ></vx-date-picker>
        </form>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button @click="emit('update:visible', false)" size="lg" color="muted" data-test="schedule-cancel">
                    Cancel
                </vx-button>
                <vx-button
                    form="schedule-group-status-update"
                    type="submit"
                    size="lg"
                    color="primary"
                    class="lg:grow"
                    :loading="props.loading"
                    data-test="schedule-submit"
                >
                    Schedule
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxModal, VxButton, VxDatePicker } from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import dayjs from '~/utils/dayjs';
import { ET_TIMEZONE } from '~/utils/date';
import { useInterval } from '@vueuse/core';
import { useToasted } from '~/composables/useToasted';
import {
    GROUP_STATUS_ACTIVE,
    GROUP_STATUS_CLOSED,
    GROUP_STATUS_DRAFT,
    type GroupStatus,
} from '~/constants/groupStatuses';

type ScheduleTargetStatus = Exclude<GroupStatus, typeof GROUP_STATUS_DRAFT>;

const props = withDefaults(
    defineProps<{
        visible: boolean;
        targetStatus: ScheduleTargetStatus;
        scheduledDate?: string | null;
        loading?: boolean;
    }>(),
    {
        scheduledDate: null,
        loading: false,
    }
);

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void;
    (e: 'schedule', scheduledDate: string): void;
}>();

const allowsToday = computed(() => props.targetStatus !== GROUP_STATUS_ACTIVE);

const defaultDate = () => {
    if (props.scheduledDate) {
        return dayjs.tz(props.scheduledDate, 'YYYY-MM-DD', ET_TIMEZONE).startOf('day').toDate();
    }
    return dayjs()
        .tz(ET_TIMEZONE)
        .add(allowsToday.value ? 0 : 1, 'day')
        .startOf('day')
        .toDate();
};

const date = ref<Date>(defaultDate());
const tick = useInterval(60 * 60 * 1000, { controls: true });

watch(
    () => props.visible,
    (visible) => {
        if (visible) {
            tick.resume();
        } else {
            tick.pause();
        }
    },
    { immediate: true }
);

const computeBounds = () => {
    const now = dayjs().tz(ET_TIMEZONE);
    return {
        minDate: now
            .add(allowsToday.value ? 0 : 1, 'day')
            .startOf('day')
            .toDate(),
        maxDate: now.add(90, 'days').startOf('day').toDate(),
    };
};

const bounds = ref(computeBounds());
watch([tick.counter, allowsToday, () => props.visible], () => {
    bounds.value = computeBounds();
});

const dateFormatted = computed(() => dayjs(date.value).tz(ET_TIMEZONE).format('MMM DD, YYYY'));

const timingDescription = computed(() => {
    if (props.targetStatus === GROUP_STATUS_ACTIVE) {
        return `Activates at the start of ${dateFormatted.value}`;
    }
    const action = props.targetStatus === GROUP_STATUS_CLOSED ? 'Closes' : 'Suspends';
    return `${action} at the end of ${dateFormatted.value}`;
});

watch([() => props.visible, () => props.scheduledDate, () => props.targetStatus], () => {
    date.value = defaultDate();
});

const toasted = useToasted();

const submit = () => {
    const picked = dayjs(date.value).tz(ET_TIMEZONE);
    if (picked.isBefore(dayjs(bounds.value.minDate), 'day') || picked.isAfter(dayjs(bounds.value.maxDate), 'day')) {
        const earliest = allowsToday.value ? 'today' : 'tomorrow';
        toasted.error(`Please pick a date between ${earliest} and 90 days from now.`);
        return;
    }
    emit('schedule', picked.format('YYYY-MM-DD'));
};
</script>
